// The Cash Handling split, as an admin reads it — `05 Rules/Cash Handling
// Rules` (approved, prefix CASH) and `10 Therapist End/Therapist Earnings,
// Cash, and Payout` § Cash Handling.
//
// ⚠ ITS OWN FILE FOR THE REASON `cash.custody.ts` AND `cash.handoffs.ts` ARE:
// pure functions over a row, kept away from `cash.hooks.ts` because that file
// imports the Supabase client and the Edge caller, and the admin vitest setup
// does not resolve the app's `@/` alias.
//
// ⚠ WHY AN ADMIN NEEDS THIS AT ALL. CASH's Visibility section: "Admin and Hub
// Manager may see the complete Cash Handling amount, status, confirmations,
// issue report, BALANCE UPDATE, and audit information within their access
// scope." Until now the balance update existed only as a table nothing read.
// `0203` stores all six numbers rather than just the answer, and says why in as
// many words — "a disputed handoff is argued from these four numbers". Without
// a surface, an admin arbitrating a dispute sees ₱349.50 and has no way to see
// why it is not ₱698, which is the only question a dispute asks.
//
// ⚠ NOT GRANTED TO HUB MANAGER, AND THAT IS NOT MINE TO CHANGE. `0203` grants
// `therapist_cash_retentions` to therapist, admin, executive and superadmin —
// hub_manager is absent. CASH names Admin *and* Hub Manager, so either the
// grant is an oversight or "within their access scope" is doing that work;
// `0130` already reasoned deliberately against giving a hub manager a
// per-therapist money aggregate. Building the capability and gating the
// authority is `never-invent-policy.md` §2, so this is admin/superadmin only
// and the gap is recorded as OQ-129 rather than closed with a GRANT.

/** One row of `therapist_cash_retentions` (0203). */
export interface CashRetention {
  bookingId: string;
  finalCashCollected: number;
  serviceEarnings: number;
  surchargeShare: number;
  availableBefore: number;
  retainedPhp: number;
  cashToPay: number;
}

export type RetentionLineKind = "basis" | "deduction" | "result";

export interface RetentionLine {
  label: string;
  /** What the number means, in the page's terms — never a restatement. */
  note: string;
  value: number;
  kind: RetentionLineKind;
}

/**
 * The formula, in the order the approved page writes it:
 *
 *     positive_available_balance = max(₱0, available_balance_before_cash_receipt)
 *     cash_to_pay = max(₱0,
 *         final_cash_collected
 *       - therapist_service_earnings
 *       - eligible_therapist_surcharge_share
 *       - positive_available_balance)
 *
 * ⚠ ARITHMETIC IS NEVER DONE HERE. Every number is a stored column; `0203`
 * records the terms precisely so a dispute is argued from the ledger rather
 * than from a recomputation that could disagree with it. If these six ever fail
 * to add up, the ledger is what is wrong and a display that "fixed" it silently
 * would hide that — `tcr_halves_are_the_whole` is the constraint that makes it
 * impossible, and this file must not become a second, softer one.
 */
export function retentionBreakdown(r: CashRetention): RetentionLine[] {
  return [
    {
      label: "Client cash collected",
      // ⚠ The FULL client payment — a different thing from Cash to Pay, and the
      // page separates them by name. This is the only place an admin sees it
      // beside the excess, which is the comparison a dispute turns on.
      note: "The whole amount she took from the client",
      value: r.finalCashCollected,
      kind: "basis",
    },
    {
      label: "Her service earnings",
      note: "Commission on the full promo price, at her own rate",
      value: r.serviceEarnings,
      kind: "deduction",
    },
    {
      label: "Her Long-Distance share",
      note: "Only if she qualified — ₱0 when she did not",
      value: r.surchargeShare,
      kind: "deduction",
    },
    {
      label: "Balance she was already owed",
      note: "Her positive Available Balance before this booking",
      value: r.availableBefore,
      kind: "deduction",
    },
    {
      label: "Kept as payment",
      note: "Subtracted from her Available Balance, so payroll cannot pay it twice",
      value: r.retainedPhp,
      kind: "result",
    },
    {
      label: "Cash to Pay",
      note: "The excess — the only amount that becomes a remittance",
      value: r.cashToPay,
      kind: "result",
    },
  ];
}

/**
 * Why the hand-off is smaller than the client's payment, in one sentence.
 *
 * ⚠ THE ₱0 CASE GETS ITS OWN SENTENCE. It is the common one — on the pilot's
 * data, five cash bookings in six — and "Cash to Pay ₱0.00" with no explanation
 * reads as a fault rather than as a settled booking.
 */
export function retentionSummary(r: CashRetention): string {
  if (r.cashToPay <= 0) {
    return "She was owed at least as much as she collected, so she kept all of it and there is nothing to hand over. The remittance stays open as the record that Cash Handling ran.";
  }
  if (r.retainedPhp <= 0) {
    return "Bednbody owed her nothing against this booking, so the whole collection travels onward.";
  }
  return "She keeps what Bednbody owed her out of the cash in hand and passes on the rest.";
}

/** Maps a raw row from the wire. `numeric(10,2)` crosses it as a string. */
export function toCashRetention(row: Record<string, unknown>): CashRetention {
  const n = (v: unknown) => Number(v ?? 0);
  return {
    bookingId: row.booking_id as string,
    finalCashCollected: n(row.final_cash_collected),
    serviceEarnings: n(row.service_earnings),
    surchargeShare: n(row.surcharge_share),
    availableBefore: n(row.available_before),
    retainedPhp: n(row.retained_php),
    cashToPay: n(row.cash_to_pay),
  };
}
